import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiDelete } from "../api/http";
import { useAuth } from "./AuthContext";
import { normalizeSiteUrl } from "../utils/urlValidation";

export const ActiveSiteContext = createContext(null);

export function ActiveSiteProvider({ children }) {
  const { isAuthenticated } = useAuth();
  
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  
  const [activeSite, setActiveSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectProject = useCallback((project) => {
    setActiveProject(project);
    if (project) {
      localStorage.setItem("currentProjectId", project.id);
      setActiveSite({ id: project.id, url: project.url || "", label: project.name });
    } else {
      localStorage.removeItem("currentProjectId");
      setActiveSite(null);
    }
  }, []);

  const selectWorkspace = useCallback(async (workspace) => {
    if (!workspace) {
      setActiveWorkspace(null);
      localStorage.removeItem("currentWorkspaceId");
      setProjects([]);
      setActiveProject(null);
      localStorage.removeItem("currentProjectId");
      setActiveSite(null);
      return;
    }

    setActiveWorkspace(workspace);
    localStorage.setItem("currentWorkspaceId", workspace.id);
    localStorage.removeItem("currentProjectId");
    setActiveProject(null);
    setActiveSite(null);

    try {
      const { data } = await apiGet("/api/projects");
      const projList = Array.isArray(data) ? data : [];
      setProjects(projList);

      const storedProjectId = localStorage.getItem("currentProjectId");
      let foundProj = projList.find((p) => p.id === storedProjectId);
      if (!foundProj && projList.length > 0) {
        foundProj = projList[0];
      }

      if (foundProj) {
        setActiveProject(foundProj);
        localStorage.setItem("currentProjectId", foundProj.id);
        setActiveSite({ id: foundProj.id, url: foundProj.url || "", label: foundProj.name });
      } else {
        setActiveProject(null);
        localStorage.removeItem("currentProjectId");
        setActiveSite(null);
      }
    } catch (err) {
      console.error("Failed to load projects for workspace", err);
    }
  }, []);

  const refreshWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setActiveWorkspace(null);
      setProjects([]);
      setActiveProject(null);
      setActiveSite(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiGet("/api/workspaces");
      const list = Array.isArray(data) ? data : [];
      setWorkspaces(list);

      const storedWsId = localStorage.getItem("currentWorkspaceId");
      let activeWs = list.find((w) => w.id === storedWsId);
      if (!activeWs) {
        localStorage.removeItem("currentProjectId");
        activeWs = list[0] || null;
      }

      if (activeWs) {
        setActiveWorkspace(activeWs);
        localStorage.setItem("currentWorkspaceId", activeWs.id);

        const projRes = await apiGet("/api/projects");
        const projList = Array.isArray(projRes.data) ? projRes.data : [];
        setProjects(projList);

        const storedProjId = localStorage.getItem("currentProjectId");
        let activeProj = projList.find((p) => p.id === storedProjId) || projList[0] || null;

        if (activeProj) {
          setActiveProject(activeProj);
          localStorage.setItem("currentProjectId", activeProj.id);
          setActiveSite({ id: activeProj.id, url: activeProj.url || "", label: activeProj.name });
        } else {
          setActiveProject(null);
          localStorage.removeItem("currentProjectId");
          setActiveSite(null);
        }
      } else {
        setActiveWorkspace(null);
        localStorage.removeItem("currentWorkspaceId");
        setProjects([]);
        setActiveProject(null);
        localStorage.removeItem("currentProjectId");
        setActiveSite(null);
      }
      setError("");
    } catch (err) {
      setError("Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshWorkspaces();
  }, [refreshWorkspaces]);

  const createWorkspace = useCallback(async (name, slug) => {
    try {
      const { data } = await apiPost("/api/workspaces", { name, slug });
      setWorkspaces((current) => [...current, data]);
      await selectWorkspace(data);
      return data;
    } catch (err) {
      setError("Failed to create workspace");
      throw err;
    }
  }, [selectWorkspace]);

  const createProject = useCallback(async (name, url) => {
    try {
      const normalizedUrl = normalizeSiteUrl(url);
      const { data } = await apiPost("/api/projects", { name, url: normalizedUrl });
      setProjects((current) => [...current, data]);
      selectProject(data);
      return data;
    } catch (err) {
      setError("Failed to create project");
      throw err;
    }
  }, [selectProject]);

  const saveSite = useCallback(async (url, label) => {
    return createProject(label || url, url);
  }, [createProject]);

  const deleteSite = useCallback(async (projectId) => {
    try {
      await apiDelete(`/api/projects/${projectId}`);
      setProjects((current) => current.filter((p) => p.id !== projectId));
      if (activeProject?.id === projectId) {
        setProjects((current) => {
          const remaining = current.filter((p) => p.id !== projectId);
          if (remaining.length > 0) {
            selectProject(remaining[0]);
          } else {
            selectProject(null);
          }
          return remaining;
        });
      }
    } catch (err) {
      setError("Failed to delete project");
      throw err;
    }
  }, [activeProject, selectProject]);

  return (
    <ActiveSiteContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        selectWorkspace,
        createWorkspace,
        projects,
        activeProject,
        selectProject,
        createProject,
        activeSite,
        selectSite: selectProject,
        saveSite,
        deleteSite,
        sites: { saved: projects, recent: [] },
        loading,
        error,
        refreshSites: refreshWorkspaces,
      }}
    >
      {children}
    </ActiveSiteContext.Provider>
  );
}

export function useActiveSite() {
  return useContext(ActiveSiteContext);
}
